<template>
  <!-- Die Haupt-Chat-Komponente. Die Klasse 'general' wird für den globalen Chat verwendet. -->
  <section :class="['chat-view', { general: props.general }]">
    <!-- Container für die Nachrichten, ermöglicht das Scrollen -->
    <div ref="messagesWrap" class="messages-wrap">
      <!-- Iteriert über alle Nachrichten und zeigt sie an -->
      <div v-for="msg in messages" :key="msg.id" class="message" :class="{ 'is-own': isOwnMessage(msg) }">
        <img class="avatar" :src="getAvatarUrl(msg)" @error="onImageError" alt=""/>
        <div class="message-content">
          <!-- Metadaten der Nachricht: Autor und Zeit -->
          <div class="meta">
            <span class="author">{{ msg.author?.nickname || msg.sender || (msg.ip ? "Anonymer Gast" : null) || "Unbekannt" }}</span>
            <span class="time">{{ formatTime(msg.time) }}</span>
          </div>
          <div class="body">
            {{ msg.text }}
            <div class="attachments" v-if="msg.attachments?.length">
              <!-- Zeigt Anhänge an, falls vorhanden -->
              <UploadedDateiView v-for="fileRef in msg.attachments" :key="fileRef" :fileRef="fileRef"/>
            </div>
          </div>
        </div>
      </div>
      <!-- Nachricht, die angezeigt wird, wenn der Chat leer ist -->
      <div v-if="messages.length === 0" class="empty-chat-message">
        <div class="icon">💬</div>
        <p>Noch keine Nachrichten.</p>
        <span>Schreibe die erste Nachricht!</span>
      </div>
    </div>

    <!-- Eingabebereich für neue Nachrichten -->
    <div class="composer">
      <button class="attach-btn" type="button" @click="openAttachment" aria-label="Anhang hinzufügen">+</button>
      <textarea ref="textareaRef" :maxlength="MAX_CHARS" v-model="text" @keydown.enter.shift.prevent="handleSend" :placeholder="placeholderText" class="composer-input" aria-label="Nachricht" rows="1"></textarea>
      <span class="char-counter" :class="{ warning: text.length >= MAX_CHARS - 50, limit: text.length >= MAX_CHARS }">{{ text.length }}/{{ MAX_CHARS }}</span>
      <button class="send-btn" @click="handleSend" :disabled="sending" aria-label="Senden">
        <svg class="send-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
          <path d="M2 21l20-9L2 3v7l15 2-15 2v7z" fill="currentColor"/>
        </svg>
      </button>
    </div>
    <!-- Verstecktes Input-Feld für den Datei-Upload -->
  </section>
  <input ref="fileInput" type="file" class="hidden-file-input" @change="handleFileSelected"/>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '../composables/useAuth';
import { sendMessage, subscribeToMessages } from '../services/chat.js';
import { DEFAULT_PROFILE_IMAGE } from '../assets/config';
import { resolveApiUrl } from '../services/api';
import { uploadLocalFile } from '../services/localFiles';
import UploadedDateiView from "./UploadedDateiView.vue";

// Holt den Router und den globalen Auth-Status.
const route = useRoute();
const { user } = useAuth();

const MAX_CHARS = 800;

const props = defineProps({ chatIdProp: { type: String, default: null }, general: {type: Boolean, default: false}})
const chatId = computed(() => props.chatIdProp || route.params.chatId || '')

// Lokale reaktive Zustandsvariablen für die Komponente.
const messages = ref([])
const text = ref('')
const sending = ref(false)
const messagesWrap = ref(null)
const textareaRef = ref(null)

// async iterator handle
let iterator = null
let abortController = null

// Dynamischer Platzhaltertext für das Eingabefeld.
const placeholderText = computed(() => (chatId.value ? 'Nachricht schreiben... (Shift+Enter zum Senden)' : 'Kein Chat ausgewählt'))

/**
 * Prüft, ob eine Nachricht vom aktuell eingeloggten Benutzer stammt.
 * @param {object} msg - Das Nachrichtenobjekt.
 * @returns {boolean}
 */
const isOwnMessage = (msg) => {
  if (!user.value || !msg.sender) return false
  return msg.sender === user.value.email
}

function onImageError(e) {
  const img = e.target
  if (img && img.src !== DEFAULT_PROFILE_IMAGE) img.src = DEFAULT_PROFILE_IMAGE
}

const fileInput = ref(null)


/**
 * Öffnet den Datei-Auswahldialog.
 */
function openAttachment() {
  fileInput.value?.click()
}
/**
 * Handler, der aufgerufen wird, wenn eine Datei für den Upload ausgewählt wurde.
 * @param {Event} event - Das 'change'-Event des Datei-Inputs.
 */
async function handleFileSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return
  try {
    const fileRef = await uploadLocalFile(file)
    await sendMessage(chatId.value, file.name, [fileRef])
  } catch (e) {
    console.error("Fehler beim Senden der Datei-Nachricht:", e);
  }
}

/**
 * Formatiert einen Zeitstempel in ein lesbares Zeitformat (HH:MM).
 * @param {number} ts - Der Zeitstempel in Millisekunden.
 * @returns {string}
 */
function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Löst die Avatar-URL für eine Nachricht auf.
 * @param {object} msg - Das Nachrichtenobjekt.
 * @returns {string} Die vollständige URL zum Avatar.
 */
const getAvatarUrl = (msg) => {
  const raw = msg.author?.profileimage || DEFAULT_PROFILE_IMAGE;
  return resolveApiUrl(raw)
}

// Easter Egg :)
function doABarrelRoll() {
  const body = document.body;
  body.style.transition = "transform 1s ease-in-out";
  body.animate([{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }], {duration: 1000, easing: "ease-in-out"});
}

/**
 * Handler zum Senden einer neuen Nachricht.
 */
async function handleSend() {
  if (!chatId.value || !text.value.trim()) return;
  if (text.value.length > MAX_CHARS) return;
  sending.value = true
  const body = text.value.trim();
  const lower = body.replace(" ", "").toLowerCase();
  if(lower.includes("do") && lower.includes("a") && lower.includes("roll")) doABarrelRoll();
  try {
    const sent = await sendMessage(chatId.value, body)
    if (sent) messages.value.push(sent)
    text.value = ''
  } catch (e) {
    console.error('Senden fehlgeschlagen', e)
  } finally {
    sending.value = false
  }
}

/**
 * Scrollt den Nachrichtenbereich nach unten.
 * @param {boolean} [smooth=true] - Ob das Scrollen animiert sein soll.
 */
function scrollToBottom(smooth = true) {
  const el = messagesWrap.value
  if (!el) return
  const top = el.scrollHeight - el.clientHeight
  el.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' })
}

// Beobachtet das Textarea-Feld, um seine Höhe dynamisch an den Inhalt anzupassen.
watch(text, () => {
  const el = textareaRef.value
  if (el) {
    // Reset height to shrink if text is deleted
    el.style.height = 'auto'
    // Set height to scroll height to grow
    el.style.height = `${el.scrollHeight}px`
  }
})

// Beobachtet die Nachrichtenliste und scrollt bei neuen Nachrichten automatisch nach unten.
watch(messages, () => {
  setTimeout(() => scrollToBottom(true), 40)
}, { deep: true })

/**
 * Startet den asynchronen Generator, der kontinuierlich neue Nachrichten vom Server abruft.
 * @param {string} chatIdValue - Die ID des zu abonnierenden Chats.
 */
async function startIterator(chatIdValue) {
  if (!chatIdValue) return
  try {
    abortController = new AbortController()
    iterator = await subscribeToMessages(chatIdValue, abortController.signal)
  } catch (e) {
    console.error('Fehler beim Erhalten des Iterators', e)
    iterator = null
    return
  }

  // Verarbeitet den Generator im Hintergrund, ohne die UI zu blockieren.
  (async () => {
    try {
      for await (const msg of iterator) {
        // msg kann ein einzelnes Objekt oder Array sein
        if (Array.isArray(msg)) messages.value.push(...msg)
        else messages.value.push(msg)
      }
    } catch (e) {
      // falls iterator.throw() oder Netzwerkfehler
      console.error('Fehler beim Streamen der Nachrichten', e)
    } finally {
      // iterator beendet oder return() aufgerufen
      iterator = null
    }
  })()
}

/**
 * Stoppt den Nachrichten-Generator sicher.
 */
async function stopIterator() {
  abortController?.abort()
  abortController = null
  iterator = null
}

// Beobachtet die `chatId`. Wenn sie sich ändert, wird der alte Chat gestoppt und der neue gestartet.
watch(chatId, async (id) => {
  await stopIterator()
  messages.value = []
  if (id) await startIterator(id)
})

// Lifecycle-Hook: Startet den Chat, wenn die Komponente eingehängt wird.
onMounted(async () => {
  if (chatId.value) await startIterator(chatId.value)
})

// Lifecycle-Hook: Stoppt den Chat, bevor die Komponente zerstört wird, um Ressourcen freizugeben.
onBeforeUnmount(async () => {
  await stopIterator()
})
</script>

<style scoped lang="scss">
.chat-view {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.chat-view.general {
  height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
}

.messages-wrap {
  flex: 1 1 auto;
  min-height: 150px;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  scroll-behavior: smooth;
}

.empty-chat-message {
  margin: auto;
  text-align: center;
  color: rgba(255,255,255,0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;

  .icon {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    font-weight: 600;
  }
}

.message {
  max-width: 85%;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  background: rgba(255,255,255,0.03);
  align-self: flex-start; /* Nachrichten linksbündig; du kannst nach author ausrichten */
  color: inherit;
}

.message.is-own {
  align-self: flex-end;
}

.meta {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.6);
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.author { font-weight: 600; }
.body {
  white-space: pre-wrap; word-break: break-word; text-align: left;
  height: auto;
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Composer (Input + Send) */
.composer {
  display: flex;
  gap: 0.6rem;
  padding: 0.6rem;
  border-top: 1px solid rgba(255,255,255,0.04);
  background: rgba(0,0,0,0.02);
  align-items: center;
}

.hidden-file-input {
  display: none;
}

.composer-input {
  flex: 1 1 auto;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
  color: inherit;
  resize: none;
  overflow-y: hidden;
  line-height: 1.5;
  outline: none;
  font-size: 0.95rem;
}

.send-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: none;
  background: rgba(21,112,254,0.95);
  color: white;
  cursor: pointer;
  padding: 0;
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* SVG-Sizing */
.send-icon {
  width: 18px;
  height: 18px;
  display: block;
}

.messages-wrap {
  overflow-y: auto;

  /* Firefox */
  scrollbar-width: none;

  /* IE + Edge */
  -ms-overflow-style: none;
}

.messages-wrap::-webkit-scrollbar {
  display: none;
}

.message {
  max-width: 85%;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  background: rgba(255,255,255,0.03);
  align-self: flex-start;
  color: inherit;

  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}

.message.is-own {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-content {
  min-width: 0;
  flex: 1;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 2px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
}

.attach-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
  color: rgba(255,255,255,0.85);

  font-size: 22px;
  font-weight: 500;
  cursor: pointer;

  transition: 0.15s ease;
}

.attach-btn:hover {
  background: rgba(255,255,255,0.05);
  transform: translateY(-1px);
}

.attach-btn:active {
  transform: scale(0.97);
}

.char-counter {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  white-space: nowrap;
  min-width: 3rem;
  text-align: right;
}

.char-counter.warning {
  color: #f59e0b;
}
.char-counter.limit {
  color: #ef4444;
  font-weight: 700;
}

</style>
